import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useCart } from '../hooks/useCart'
import { useAuth } from '../hooks/useAuth'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { useShipping } from '../hooks/useShipping'
import { useDeliveryDistance } from '../hooks/useDeliveryDistance'
import { createCheckoutOrder, getOrder, updateOrder } from '../services/orders.service'
import { getCustomerProfileByEmail } from '../services/customers.service'
import { getImageUrl } from '../services/cloudinary.service'
import { createMercadoPagoPreference } from '../services/mercadopago.service'
import { getProductStockSummary } from '../services/stock.service'
import { formatCurrency, isValidEmail, normalizeCountryName } from '../utils/helpers'
import { normalizeWhatsAppPhoneAR } from '../utils/whatsapp'
import { getStockForSelection } from '../utils/stock'
import { notifyOrderCreated } from '../services/notifications.service'
import Toast from '../components/Toast'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import FulfillmentSelector from '../components/FulfillmentSelector'
import './Checkout.css'

/**
 * Página Checkout
 * Proceso de checkout con formulario de datos del cliente
 * Adaptado al diseño del mockup "Pine & Co"
 */
function Checkout() {
  const navigate = useNavigate()
  const { cartItems, getSubtotal, getShippingQuote, clearCart } = useCart()
  const { currentUser, userData } = useAuth()
  const { rules } = useBusinessRulesContext()
  const { settings: shippingSettings } = useShipping()
  const outOfStockPolicy = rules?.outOfStockPolicy || { visibility: 'show', purchaseBehavior: 'block_purchase', inquiryChannel: 'whatsapp', labelText: 'Sin stock' }

  // Estado del formulario
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    selectedPhoneIndex: null,
    address: {
      street: '',
      apartment: '',
      city: '',
      postalCode: '',
      province: '',
      country: 'ARGENTINA'
    },
    selectedAddressIndex: null,
    notes: ''
  })

  // Estado para nombres separados (visual)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')

  // Datos del cliente guardados
  const [customerData, setCustomerData] = useState(null)
  const preloadRef = useRef({ email: null })

  // Estado para nuevo teléfono/dirección
  const [useNewPhone, setUseNewPhone] = useState(false)
  const [newPhone, setNewPhone] = useState('')
  const [useNewAddress, setUseNewAddress] = useState(false)
  const [newAddress, setNewAddress] = useState({
    street: '',
    apartment: '',
    city: '',
    postalCode: '',
    province: '',
    country: 'ARGENTINA'
  })

  // Estado de validación
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [notification, setNotification] = useState(null)
  const [mercadoPagoRetryOrderId, setMercadoPagoRetryOrderId] = useState(null)
  const [fulfillmentSelection, setFulfillmentSelection] = useState({ method: 'delivery', pickupWarehouseId: '' })

  const enabledPaymentMethods = useMemo(() => {
    if (rules?.paymentMethods?.enabled === false) return []
    const methods = Array.isArray(rules?.paymentMethods?.methods) ? rules.paymentMethods.methods : []
    return methods.filter(m => m?.enabled !== false)
  }, [rules])

  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('')

  useEffect(() => {
    if (!enabledPaymentMethods.length) {
      if (selectedPaymentMethodId) setSelectedPaymentMethodId('')
      return
    }
    const exists = enabledPaymentMethods.some(m => m.id === selectedPaymentMethodId)
    if (!selectedPaymentMethodId || !exists) setSelectedPaymentMethodId(enabledPaymentMethods[0].id)
  }, [enabledPaymentMethods, selectedPaymentMethodId])

  const selectedPaymentMethod = useMemo(() => {
    if (!selectedPaymentMethodId) return null
    return enabledPaymentMethods.find(m => m.id === selectedPaymentMethodId) || null
  }, [enabledPaymentMethods, selectedPaymentMethodId])

  const traceParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('trace') || params.get('debug')
    const enabled = raw === '1' || raw === 'true'
    const traceId = params.get('traceId') || params.get('trace_id')
    return { enabled, traceId: traceId || null }
  }, [])

  useEffect(() => {
    if (selectedPaymentMethod?.type !== 'gateway_mercadopago') {
      setMercadoPagoRetryOrderId(null)
    }
  }, [selectedPaymentMethod?.type])

  const calculatePaymentAdjustment = useCallback((subtotal, priceAdjustment) => {
    if (!priceAdjustment) return 0
    const rawValue = Number(priceAdjustment.value)
    if (!Number.isFinite(rawValue) || rawValue === 0) return 0
    const baseAmount = priceAdjustment.kind === 'fixed' ? rawValue : (subtotal * rawValue) / 100
    const sign = priceAdjustment.sign === 'surcharge' ? 1 : -1
    return baseAmount * sign
  }, [])

  const buildOrderSnapshot = (source) => {
    if (!source) return null
    const total = Number(source.total)
    if (!Number.isFinite(total) || total <= 0) return null
    return {
      total,
      orderNumber: source.orderNumber || null,
      customerEmail: source.customer?.email || null,
      paymentType: source.payment?.type || null
    }
  }

  const buildGatewayPayload = (mp) => ({
    provider: 'mercadopago',
    preferenceId: mp?.preferenceId || null,
    initPoint: mp?.initPoint || null,
    sandboxInitPoint: mp?.sandboxInitPoint || null,
    status: 'created'
  })

  const displaySubtotal = getSubtotal()
  const shippingSettingsFulfillment = shippingSettings?.fulfillment || {}
  const negotiationEnabled = shippingSettings?.distancePricing?.negotiationEnabled === true || shippingSettings?.distancePricing?.negotiationEnabled === 'true'
  const availableFulfillmentMethods = useMemo(() => {
    const out = []
    if (shippingSettingsFulfillment.homeDeliveryEnabled !== false) out.push('delivery')
    if (shippingSettingsFulfillment.pickupEnabled === true) out.push('pickup')
    if (negotiationEnabled) out.push('negotiation')
    return out
  }, [negotiationEnabled, shippingSettingsFulfillment.homeDeliveryEnabled, shippingSettingsFulfillment.pickupEnabled])

  const { distance, status: distanceStatus, shouldComputeDistance, confirm: confirmDeliveryAddress, isConfirmed: isDeliveryConfirmed, canConfirm: canConfirmDeliveryAddress } = useDeliveryDistance({
    shippingSettings,
    address: formData.address,
    method: fulfillmentSelection.method
  })

  const shippingQuote = getShippingQuote(distance)
  const forcedMethod = shippingQuote.type === 'negotiation' ? 'negotiation' : null
  const effectiveMethod = forcedMethod || fulfillmentSelection.method
  const selectedMethod = fulfillmentSelection.method
  const isPickup = effectiveMethod === 'pickup'
  const isNegotiation = effectiveMethod === 'negotiation'
  const displayShipping = isPickup || isNegotiation || shippingQuote.type === 'pending' ? 0 : shippingQuote.cost
  const displayAdjustment = selectedPaymentMethod?.priceAdjustment
    ? calculatePaymentAdjustment(displaySubtotal, selectedPaymentMethod.priceAdjustment)
    : 0
  const displayTotal = Math.max(0, displaySubtotal + displayAdjustment) + displayShipping
  const deliveryReadyForPayment = selectedMethod !== 'delivery'
    ? true
    : isDeliveryConfirmed && distanceStatus.state === 'ready' && shippingQuote.type !== 'pending' && shippingQuote.type !== 'disabled'
  const paymentLocked = selectedMethod === 'delivery' && !deliveryReadyForPayment
  const canProceedToPay = !submitting && (
    effectiveMethod === 'pickup'
      ? !!fulfillmentSelection.pickupWarehouseId
      : selectedMethod === 'delivery'
        ? deliveryReadyForPayment
        : true
  )

  // Sincronizar nombre completo cuando cambian las partes
  useEffect(() => {
    const fullName = `${firstName} ${lastName}`.trim()
    setFormData(prev => ({ ...prev, name: fullName }))
  }, [firstName, lastName])

  const loadCustomerData = useCallback(async () => {
    if (!currentUser?.email) return
    try {
      const email = String(currentUser.email).trim().toLowerCase()
      if (!email) return
      if (preloadRef.current.email === email) return
      preloadRef.current.email = email

      const namePartsFallback = (userData?.displayName || '').split(' ')
      setFirstName(prev => prev || (namePartsFallback[0] || ''))
      setLastName(prev => prev || (namePartsFallback.slice(1).join(' ') || ''))
      setFormData(prev => ({
        ...prev,
        name: userData?.displayName || prev.name,
        email: email || prev.email,
        phone: userData?.phone || prev.phone
      }))

      const cacheKey = `pinohome_customer_profile:${email}`
      const cachedRaw = localStorage.getItem(cacheKey)
      if (cachedRaw) {
        try {
          const cached = JSON.parse(cachedRaw)
          setCustomerData(cached)
          const primaryPhone = cached.phones && cached.phones.length > 0
            ? cached.phones[cached.primaryPhoneIndex || 0]
            : ''
          const primaryAddress = cached.addresses && cached.addresses.length > 0
            ? cached.addresses[cached.primaryAddressIndex || 0]
            : null

          const nameParts = (cached.name || userData?.displayName || '').split(' ')
          setFirstName(nameParts[0] || '')
          setLastName(nameParts.slice(1).join(' ') || '')

          setFormData(prev => ({
            ...prev,
            name: cached.name || userData?.displayName || prev.name,
            email: email || prev.email,
            phone: primaryPhone || prev.phone,
            selectedPhoneIndex: cached.primaryPhoneIndex !== undefined ? cached.primaryPhoneIndex : null,
            address: primaryAddress
              ? { ...prev.address, ...primaryAddress, country: normalizeCountryName(primaryAddress.country || prev.address.country || 'ARGENTINA') || 'ARGENTINA' }
              : prev.address,
            selectedAddressIndex: cached.primaryAddressIndex !== undefined ? cached.primaryAddressIndex : null
          }))
        } catch (e) {
          console.error('Error leyendo cache de cliente:', e)
        }
      }

      const customer = await getCustomerProfileByEmail(email)
      
      if (customer) {
        setCustomerData(customer)
        
        // Establecer valores por defecto
        const primaryPhone = customer.phones && customer.phones.length > 0 
          ? customer.phones[customer.primaryPhoneIndex || 0] 
          : ''
        const primaryAddress = customer.addresses && customer.addresses.length > 0
          ? customer.addresses[customer.primaryAddressIndex || 0]
          : null

        // Parsear nombre
        const nameParts = (customer.name || userData?.displayName || '').split(' ')
        setFirstName(nameParts[0] || '')
        setLastName(nameParts.slice(1).join(' ') || '')

        setFormData(prev => ({
          ...prev,
          name: customer.name || userData?.displayName || prev.name,
          email: email || prev.email,
          phone: primaryPhone || prev.phone,
          selectedPhoneIndex: customer.primaryPhoneIndex !== undefined ? customer.primaryPhoneIndex : null,
          address: primaryAddress
            ? { ...prev.address, ...primaryAddress, country: normalizeCountryName(primaryAddress.country || prev.address.country || 'ARGENTINA') || 'ARGENTINA' }
            : prev.address,
          selectedAddressIndex: customer.primaryAddressIndex !== undefined ? customer.primaryAddressIndex : null
        }))
        localStorage.setItem(cacheKey, JSON.stringify(customer))
      } else {
        // Si no hay datos del cliente, usar datos del usuario
        const nameParts = (userData?.displayName || '').split(' ')
        setFirstName(nameParts[0] || '')
        setLastName(nameParts.slice(1).join(' ') || '')

        setFormData(prev => ({
          ...prev,
          name: userData?.displayName || prev.name,
          email: email || prev.email,
          phone: userData?.phone || prev.phone
        }))
      }
    } catch (error) {
      console.error('Error cargando datos del cliente:', error)
    }
  }, [currentUser, userData])

  // Cargar datos del cliente si está logueado
  useEffect(() => {
    if (currentUser?.email) {
      loadCustomerData()
    }
  }, [currentUser, loadCustomerData])

  // Redirigir si el carrito está vacío
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/carrito')
    }
  }, [cartItems.length, navigate])

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target

    if (name === 'firstName') {
      setFirstName(value)
      if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
    } else if (name === 'lastName') {
      setLastName(value)
      if (errors.name) setErrors(prev => ({ ...prev, name: '' }))
    } else if (name.startsWith('address.')) {
      const addressField = name.split('.')[1]
      const nextValue = addressField === 'country' ? normalizeCountryName(value) : value
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: nextValue
        }
      }))
      if (errors[`address.${addressField}`]) {
        setErrors(prev => ({ ...prev, [`address.${addressField}`]: '' }))
      }
    } else if (name === 'selectedPhone') {
      const index = parseInt(value)
      if (index === -1) {
        setUseNewPhone(true)
        setFormData(prev => ({ ...prev, selectedPhoneIndex: null, phone: '' }))
      } else {
        setUseNewPhone(false)
        const phone = customerData?.phones[index] || ''
        setFormData(prev => ({ ...prev, selectedPhoneIndex: index, phone }))
      }
    } else if (name === 'selectedAddress') {
      const index = parseInt(value)
      if (index === -1) {
        setUseNewAddress(true)
        setFormData(prev => ({
          ...prev,
          selectedAddressIndex: null,
          address: { street: '', apartment: '', city: '', postalCode: '', province: '', country: 'ARGENTINA' }
        }))
      } else {
        setUseNewAddress(false)
        const address = customerData?.addresses[index] || {}
        setFormData(prev => ({
          ...prev,
          selectedAddressIndex: index,
          address: { ...prev.address, ...address, country: normalizeCountryName(address.country || prev.address.country || 'ARGENTINA') || 'ARGENTINA' }
        }))
      }
    } else if (name.startsWith('newAddress.')) {
      const addressField = name.split('.')[1]
      const nextValue = addressField === 'country' ? normalizeCountryName(value) : value
      setNewAddress(prev => ({
        ...prev,
        [addressField]: nextValue
      }))
      if (useNewAddress) {
        setFormData(prev => ({
          ...prev,
          address: {
            ...prev.address,
            [addressField]: nextValue
          }
        }))
      }
    } else if (name === 'newPhone') {
      setNewPhone(value)
      if (useNewPhone) {
        setFormData(prev => ({ ...prev, phone: value }))
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
      if (errors[name]) {
        setErrors(prev => ({ ...prev, [name]: '' }))
      }
    }
  }

  // Validar formulario
  const validate = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido'
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'El email no es válido'
    }

    const finalPhone = useNewPhone ? newPhone.trim() : formData.phone.trim()
    if (!finalPhone) {
      newErrors.phone = 'El teléfono es requerido'
    } else {
      const normalized = normalizeWhatsAppPhoneAR(finalPhone)
      if (!normalized.isValid) {
        newErrors.phone = 'El teléfono debe estar en formato WhatsApp 549XXXXXXXXXX'
      }
    }

    if (effectiveMethod === 'pickup' && !fulfillmentSelection.pickupWarehouseId) {
      newErrors.fulfillment = 'Selecciona una sucursal para retirar'
    }
    if (availableFulfillmentMethods.length === 0) {
      newErrors.fulfillment = 'No hay métodos de entrega disponibles'
    } else if (shippingQuote.type === 'disabled') {
      newErrors.fulfillment = 'Los envíos están deshabilitados en la configuración'
    }

    if (selectedMethod === 'delivery') {
      const street = String(formData.address?.street || '').trim()
      const city = String(formData.address?.city || '').trim()
      const province = String(formData.address?.province || '').trim()
      const country = String(formData.address?.country || '').trim()
      if (!street) newErrors['address.street'] = 'La calle es requerida para envío a domicilio'
      if (!city) newErrors['address.city'] = 'La ciudad es requerida para envío a domicilio'
      if (!province) newErrors['address.province'] = 'La provincia es requerida para envío a domicilio'
      if (!country) newErrors['address.country'] = 'El país es requerido para envío a domicilio'
      if (!isDeliveryConfirmed) {
        newErrors.address = 'Confirmá tu dirección de entrega para calcular el envío'
      } else if (distanceStatus.state === 'error') {
        newErrors.address = distanceStatus.message || 'No se pudo calcular el envío'
      } else if (shouldComputeDistance && street && city && province && distanceStatus.state !== 'ready') {
        newErrors.address = distanceStatus.message || 'Estamos calculando tu costo de envío…'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (submitting) return

    if (!validate()) {
      setNotification({
        type: 'error',
        message: 'Por favor completa todos los campos requeridos correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    if (outOfStockPolicy.purchaseBehavior !== 'allow_purchase') {
      const summaries = new Map()
      const hasVariantBreakdown = (summary) => {
        const byColor = summary?.byColor || {}
        const bySize = summary?.bySize || {}
        const byColorSize = summary?.byColorSize || {}
        return Object.keys(byColor).length > 0 || Object.keys(bySize).length > 0 || Object.keys(byColorSize).length > 0
      }
      const getSummary = async (productId) => {
        if (!productId) return null
        if (summaries.has(productId)) return summaries.get(productId)
        try {
          const summary = await getProductStockSummary(productId)
          summaries.set(productId, summary)
          return summary
        } catch {
          summaries.set(productId, null)
          return null
        }
      }

      const liveStocks = await Promise.all(
        cartItems.map(async (item) => {
          const variant = item?.variant
          const selection = variant?.type === 'fashion' ? variant?.selection : null
          if (selection && (selection.color || selection.size)) {
            const summary = await getSummary(item.productId)
            if (summary) {
              const stock = hasVariantBreakdown(summary)
                ? getStockForSelection(summary, { color: selection.color, size: selection.size })
                : Number(summary.total || 0) || 0
              return { id: item.id, stock }
            }
          }
          return { id: item.id, stock: Number(item?.stock || 0) || 0 }
        })
      )

      const stockMap = new Map(liveStocks.map(s => [s.id, s.stock]))
      const invalid = cartItems.filter(i => {
        const stock = stockMap.get(i.id) ?? (i?.stock || 0)
        return stock <= 0 || (i?.quantity || 0) > stock
      })
      if (invalid.length > 0) {
        const message = outOfStockPolicy.purchaseBehavior === 'inquiry_only'
          ? 'Hay productos sin stock. Este checkout admite solo consulta.'
          : 'Hay productos sin stock o con cantidad mayor al stock.'
        setNotification({ type: 'error', message })
        setTimeout(() => setNotification(null), 5000)
        if (outOfStockPolicy.purchaseBehavior === 'inquiry_only') {
          navigate(outOfStockPolicy.inquiryChannel === 'help_page' ? '/ayuda' : '/ayuda#contacto')
        }
        return
      }
    }

    const isMercadoPago = selectedPaymentMethod?.type === 'gateway_mercadopago'
    if (isMercadoPago && mercadoPagoRetryOrderId) {
      setSubmitting(true)
      try {
        const existingOrder = await getOrder(mercadoPagoRetryOrderId).catch(() => null)
        const orderSnapshot = buildOrderSnapshot(existingOrder)
        const mp = await createMercadoPagoPreference({
          orderId: mercadoPagoRetryOrderId,
          storefrontUrl: window.location.origin,
          orderSnapshot,
          trace: traceParams.enabled,
          traceId: traceParams.traceId
        })
        const gatewayPayload = buildGatewayPayload(mp)
        await updateOrder(mercadoPagoRetryOrderId, { paymentGateway: gatewayPayload }).catch(() => null)
        const redirectUrl = mp?.redirectUrl || mp?.sandboxInitPoint || mp?.initPoint
        if (!redirectUrl) {
          throw new Error('MercadoPago no devolvió un link de pago')
        }
        clearCart()
        window.location.href = redirectUrl
        return
      } catch (e) {
        setNotification({
          type: 'error',
          message: `No se pudo generar el link de MercadoPago: ${e?.message || 'Error desconocido'}`
        })
        setTimeout(() => setNotification(null), 6000)
        setSubmitting(false)
        return
      }
    }

    setMercadoPagoRetryOrderId(null)
    setSubmitting(true)

    try {
      // Calcular totales
      const subtotal = getSubtotal()
      const shipping = effectiveMethod === 'pickup' || effectiveMethod === 'negotiation' ? 0 : shippingQuote.cost
      const adjustmentAmount = selectedPaymentMethod?.priceAdjustment
        ? calculatePaymentAdjustment(subtotal, selectedPaymentMethod.priceAdjustment)
        : 0
      const adjustedSubtotal = Math.max(0, subtotal + adjustmentAmount)
      const total = adjustedSubtotal + shipping

      const payment = selectedPaymentMethod
        ? {
          methodId: selectedPaymentMethod.id,
          type: selectedPaymentMethod.type,
          label: selectedPaymentMethod.label,
          customerNote: selectedPaymentMethod.customerNote || '',
          priceAdjustment: selectedPaymentMethod.priceAdjustment || null,
          selectedAt: new Date()
        }
        : null

      const paymentAdjustment = selectedPaymentMethod?.priceAdjustment && adjustmentAmount
        ? { ...selectedPaymentMethod.priceAdjustment, amount: adjustmentAmount }
        : null

      // Obtener teléfono y dirección finales
      const finalPhoneRaw = useNewPhone ? newPhone.trim() : formData.phone.trim()
      const normalizedPhone = normalizeWhatsAppPhoneAR(finalPhoneRaw)
      const finalPhone = normalizedPhone.isValid ? normalizedPhone.normalized : finalPhoneRaw
      const finalAddress = useNewAddress ? newAddress : formData.address

      // Preparar datos del pedido
      const orderData = {
        customer: {
          name: formData.name.trim(),
          email: formData.email.trim(),
          phone: finalPhone,
          address: {
            street: finalAddress.street?.trim() || '',
            apartment: finalAddress.apartment?.trim() || '',
            city: finalAddress.city?.trim() || '',
            postalCode: finalAddress.postalCode?.trim() || '',
            province: finalAddress.province?.trim() || '',
            country: normalizeCountryName(finalAddress.country?.trim() || 'ARGENTINA') || 'ARGENTINA'
          }
        },
        items: cartItems.map(item => ({
          productId: item.productId,
          productName: item.productName,
          sku: item.sku,
          variant: item.variant,
          price: item.price,
          quantity: item.quantity,
          image: item.image || null
        })),
        subtotal: adjustedSubtotal,
        shipping,
        total,
        payment,
        paymentAdjustment,
        fulfillment: effectiveMethod === 'pickup'
          ? { method: 'pickup', pickupWarehouseId: fulfillmentSelection.pickupWarehouseId }
          : effectiveMethod === 'negotiation'
            ? { method: 'negotiation' }
            : { method: 'delivery' },
        notes: formData.notes.trim(),
        distance
      }

      const checkoutResponse = await createCheckoutOrder({
        orderData,
        storefrontUrl: window.location.origin,
        trace: traceParams.enabled,
        traceId: traceParams.traceId
      })
      const orderId = checkoutResponse?.orderId
      if (orderId) {
        notifyOrderCreated(orderId).catch(() => null)
      }

      if (isMercadoPago) {
        const redirectUrl = checkoutResponse?.redirectUrl
        if (!redirectUrl) {
          throw new Error('MercadoPago no devolvió un link de pago')
        }
        clearCart()
        setMercadoPagoRetryOrderId(null)
        window.location.href = redirectUrl
        return
      }

      clearCart()
      setMercadoPagoRetryOrderId(null)
      navigate(`/checkout/confirmacion/${orderId}`)
    } catch (error) {
      const fallbackOrderId = error?.data?.orderId || null
      console.error('Error creando pedido:', error)
      setNotification({
        type: 'error',
        message: `Error al procesar el pedido: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
      if (isMercadoPago && fallbackOrderId) {
        clearCart()
        setMercadoPagoRetryOrderId(fallbackOrderId)
      } else {
        setMercadoPagoRetryOrderId(null)
      }
      setSubmitting(false)
    }
  }

  const handleMercadoPagoRetry = async () => {
    if (!mercadoPagoRetryOrderId || submitting) return
    setSubmitting(true)
    try {
      const existingOrder = await getOrder(mercadoPagoRetryOrderId).catch(() => null)
      const orderSnapshot = buildOrderSnapshot(existingOrder)
      const mp = await createMercadoPagoPreference({
        orderId: mercadoPagoRetryOrderId,
        storefrontUrl: window.location.origin,
        orderSnapshot,
        trace: traceParams.enabled,
        traceId: traceParams.traceId
      })
      const gatewayPayload = buildGatewayPayload(mp)
      await updateOrder(mercadoPagoRetryOrderId, { paymentGateway: gatewayPayload }).catch(() => null)
      const redirectUrl = mp?.redirectUrl || mp?.sandboxInitPoint || mp?.initPoint
      if (!redirectUrl) {
        throw new Error('MercadoPago no devolvió un link de pago')
      }
      clearCart()
      window.location.href = redirectUrl
    } catch (e) {
      setNotification({
        type: 'error',
        message: `No se pudo generar el link de MercadoPago: ${e?.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 6000)
      setSubmitting(false)
    }
  }

  // Si el carrito está vacío, no mostrar nada
  if (cartItems.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col min-h-screen bg-background-light dark:bg-background-dark">
      {submitting && selectedPaymentMethod?.type === 'gateway_mercadopago' && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl px-8 py-6 shadow-xl max-w-sm w-[90%] text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-4 border-primary border-t-transparent animate-spin" />
            <p className="text-lg font-semibold text-text-dark mb-1">
              Redirigiendo a MercadoPago...
            </p>
            <p className="text-sm text-gray-600">
              Por favor no cierres esta ventana ni recargues la página.
            </p>
          </div>
        </div>
      )}
      <main className="flex-1 w-full mx-auto max-w-[1280px] px-4 md:px-10 py-10">
        <Toast
          isVisible={!!notification}
          message={notification?.message || ''}
          type={notification?.type || 'error'}
          onClose={() => setNotification(null)}
        />

        <nav className="mb-8 flex items-center text-sm font-light text-gray-500 dark:text-gray-400 tracking-tight">
          <Link className="hover:text-primary transition-colors" to="/">Inicio</Link>
          <span className="mx-2 text-gray-300">/</span>
          <Link className="hover:text-primary transition-colors" to="/carrito">Carrito</Link>
          <span className="mx-2 text-gray-300">/</span>
          <span className="text-text-dark dark:text-white font-normal">Información y Envío</span>
        </nav>

        <div className="flex flex-col lg:flex-row gap-12">
          <div className="flex-1 w-full flex flex-col gap-8">
            {/* Steps Indicator */}
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mb-2">
                  <span className="material-symbols-outlined text-[18px]">check</span>
                </div>
                <span className="text-xs font-medium text-primary">Carrito</span>
              </div>
              <div className="flex-1 h-[1px] bg-primary mx-4 -mt-6"></div>
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center mb-2 shadow-md shadow-primary/30">
                  <span className="text-sm font-medium">2</span>
                </div>
                <span className="text-xs font-medium text-text-dark dark:text-text-light">Detalles</span>
              </div>
              <div className="flex-1 h-[1px] bg-gray-200 dark:bg-gray-700 mx-4 -mt-6"></div>
              <div className="flex flex-col items-center opacity-50">
                <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-800 text-gray-500 flex items-center justify-center mb-2">
                  <span className="text-sm font-medium">3</span>
                </div>
                <span className="text-xs font-medium text-gray-500">Pago</span>
              </div>
            </div>

            <div className="flex items-end justify-between border-b border-gray-100 dark:border-gray-800 pb-4">
              <h1 className="text-2xl font-medium text-text-dark dark:text-text-light tracking-tight">Información de Contacto</h1>
              <span className="text-sm text-gray-500 font-light">Paso 2 de 3</span>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                    Correo Electrónico <span className="text-red-500">*</span>
                  </label>
                  <input
                    className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="ejemplo@correo.com"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  {errors.email && <span className="text-red-500 text-xs mt-1">{errors.email}</span>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                    Teléfono <span className="text-red-500">*</span>
                  </label>
                  {customerData?.phones && customerData.phones.length > 0 && (
                    <select
                      name="selectedPhone"
                      value={useNewPhone ? -1 : (formData.selectedPhoneIndex !== null ? formData.selectedPhoneIndex : '')}
                      onChange={handleChange}
                      className="w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 mb-2"
                      disabled={submitting}
                    >
                      {customerData.phones.map((phone, index) => (
                        <option key={index} value={index}>
                          {phone} {index === customerData.primaryPhoneIndex ? '(Principal)' : ''}
                        </option>
                      ))}
                      <option value={-1}>➕ Agregar nuevo teléfono</option>
                    </select>
                  )}
                  <input
                    className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="5493813333333"
                    type="tel"
                    name={useNewPhone ? 'newPhone' : 'phone'}
                    value={useNewPhone ? newPhone : formData.phone}
                    onChange={handleChange}
                    disabled={submitting || (!useNewPhone && customerData?.phones && customerData.phones.length > 0)}
                  />
                  {!errors.phone && (
                    <span className="text-gray-500 text-xs mt-1 block">
                      Formato WhatsApp requerido: 549 + c\u00f3digo de \u00e1rea + n\u00famero (ej: 5493813333333)
                    </span>
                  )}
                  {errors.phone && <span className="text-red-500 text-xs mt-1">{errors.phone}</span>}
                </div>
              </div>

              {shippingSettings?.fulfillment && (
                <div className="mb-6">
                  <FulfillmentSelector
                    shippingSettings={shippingSettings}
                    value={fulfillmentSelection}
                    onChange={(next) => {
                      setFulfillmentSelection(next)
                      if (errors.fulfillment) setErrors(prev => ({ ...prev, fulfillment: '' }))
                    }}
                    disabled={submitting}
                    availableMethods={availableFulfillmentMethods}
                    forcedMethod={forcedMethod || undefined}
                  />
                  {errors.fulfillment && <span className="text-red-500 text-xs mt-2 block">{errors.fulfillment}</span>}
                </div>
              )}

              <div className="border-t border-gray-100 dark:border-gray-800"></div>

              <div>
                <h2 className="text-xl font-medium text-text-dark dark:text-text-light tracking-tight mb-6">
                  Dirección de Envío {selectedMethod === 'pickup' ? '(no requerida para retiro)' : selectedMethod === 'negotiation' ? '(opcional para acordar el envío)' : '(obligatoria)'}
                </h2>
                <p className="text-xs text-gray-500 font-light -mt-4 mb-4">
                  {selectedMethod === 'pickup' ? 'Elegiste retiro por sucursal. No es necesario cargar una dirección de envío.' : selectedMethod === 'negotiation' ? 'Acordarás el envío con el vendedor. Podés dejar la dirección vacía.' : 'La dirección es necesaria para calcular el costo de envío.'}
                </p>
                {errors.address && <div className="text-red-500 text-xs mb-3">{errors.address}</div>}
                
                {customerData?.addresses && customerData.addresses.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Seleccionar dirección guardada
                    </label>
                    <select
                      name="selectedAddress"
                      value={useNewAddress ? -1 : (formData.selectedAddressIndex !== null ? formData.selectedAddressIndex : '')}
                      onChange={handleChange}
                      className="w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3"
                      disabled={submitting}
                    >
                      {customerData.addresses.map((addr, index) => (
                        <option key={index} value={index}>
                          {addr.street}, {addr.city} {index === customerData.primaryAddressIndex ? '(Principal)' : ''}
                        </option>
                      ))}
                      <option value={-1}>➕ Agregar nueva dirección</option>
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Nombre <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="Juan"
                      type="text"
                      name="firstName"
                      value={firstName}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Apellidos <span className="text-red-500">*</span>
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors.name ? 'border-red-500' : ''}`}
                      placeholder="Pérez"
                      type="text"
                      name="lastName"
                      value={lastName}
                      onChange={handleChange}
                      disabled={submitting}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Dirección / Calle
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors['address.street'] ? 'border-red-500' : ''}`}
                      placeholder="Calle Principal 123"
                      type="text"
                      name={useNewAddress ? 'newAddress.street' : 'address.street'}
                      value={useNewAddress ? newAddress.street : formData.address.street}
                      onChange={handleChange}
                      disabled={submitting || (!useNewAddress && customerData?.addresses && customerData.addresses.length > 0)}
                    />
                    {errors['address.street'] && <span className="text-red-500 text-xs mt-1">{errors['address.street']}</span>}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Apartamento, local, etc. (opcional)
                    </label>
                    <input
                      className="w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light"
                      placeholder="Depto 4B"
                      type="text"
                      name={useNewAddress ? 'newAddress.apartment' : 'address.apartment'}
                      value={useNewAddress ? newAddress.apartment : (formData.address.apartment || '')}
                      onChange={handleChange}
                      disabled={submitting || (!useNewAddress && customerData?.addresses && customerData.addresses.length > 0)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Ciudad
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors['address.city'] ? 'border-red-500' : ''}`}
                      placeholder="Ciudad de Buenos Aires"
                      type="text"
                      name={useNewAddress ? 'newAddress.city' : 'address.city'}
                      value={useNewAddress ? newAddress.city : formData.address.city}
                      onChange={handleChange}
                      disabled={submitting || (!useNewAddress && customerData?.addresses && customerData.addresses.length > 0)}
                    />
                    {errors['address.city'] && <span className="text-red-500 text-xs mt-1">{errors['address.city']}</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Estado / Provincia
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors['address.province'] ? 'border-red-500' : ''}`}
                      placeholder="Buenos Aires"
                      type="text"
                      name={useNewAddress ? 'newAddress.province' : 'address.province'}
                      value={useNewAddress ? newAddress.province : formData.address.province}
                      onChange={handleChange}
                      disabled={submitting || (!useNewAddress && customerData?.addresses && customerData.addresses.length > 0)}
                    />
                    {errors['address.province'] && <span className="text-red-500 text-xs mt-1">{errors['address.province']}</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      Código Postal
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors['address.postalCode'] ? 'border-red-500' : ''}`}
                      placeholder="C1043"
                      type="text"
                      name={useNewAddress ? 'newAddress.postalCode' : 'address.postalCode'}
                      value={useNewAddress ? newAddress.postalCode : formData.address.postalCode}
                      onChange={handleChange}
                      disabled={submitting || (!useNewAddress && customerData?.addresses && customerData.addresses.length > 0)}
                    />
                    {errors['address.postalCode'] && <span className="text-red-500 text-xs mt-1">{errors['address.postalCode']}</span>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                      País
                    </label>
                    <input
                      className={`w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light ${errors['address.country'] ? 'border-red-500' : ''}`}
                      name={useNewAddress ? 'newAddress.country' : 'address.country'}
                      value={useNewAddress ? newAddress.country : (formData.address.country || 'ARGENTINA')}
                      onChange={handleChange}
                      disabled={submitting}
                      placeholder="ARGENTINA"
                      type="text"
                    />
                    {errors['address.country'] && <span className="text-red-500 text-xs mt-1">{errors['address.country']}</span>}
                  </div>
                </div>
              </div>

              {selectedMethod === 'delivery' ? (
                <div className="mt-4 flex flex-col gap-2">
                  <button
                    type="button"
                    className="w-full h-12 rounded-lg font-medium tracking-tight transition-all border border-primary/30 text-primary hover:bg-primary/10 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => confirmDeliveryAddress()}
                    disabled={submitting || !canConfirmDeliveryAddress || distanceStatus.state === 'loading'}
                  >
                    <span className="material-symbols-outlined text-[20px]">location_on</span>
                    {distanceStatus.state === 'loading'
                      ? 'Calculando envío...'
                      : isDeliveryConfirmed && distanceStatus.state === 'ready'
                        ? 'Dirección confirmada'
                        : 'Confirmar dirección de entrega'}
                  </button>
                  {distanceStatus.message ? (
                    <div className={`text-xs ${distanceStatus.state === 'error' ? 'text-red-500' : 'text-gray-500'}`}>
                      {distanceStatus.message}
                    </div>
                  ) : null}
                </div>
              ) : null}

              {enabledPaymentMethods.length > 0 && (
                <div className="bg-white dark:bg-background-dark p-6 rounded-xl border border-gray-100 dark:border-gray-800">
                  <h3 className="text-lg font-semibold text-text-dark dark:text-text-light mb-4 tracking-tight">
                    Medio de Pago
                  </h3>

                  <PaymentMethodSelector
                    methods={enabledPaymentMethods}
                    selectedMethodId={selectedPaymentMethodId}
                    onSelectMethodId={setSelectedPaymentMethodId}
                    disabled={paymentLocked}
                    theme={{
                      accent: 'var(--primary-color, #c27e70)',
                      accentSoft: 'var(--bg-light, #fff7f5)',
                      text: 'var(--text-dark, #111827)',
                      muted: 'var(--text-light, #6b7280)',
                      border: 'rgba(0,0,0,0.08)',
                      surface: 'var(--card-bg, #ffffff)',
                      surfaceMuted: 'var(--bg-light, #f8f7f6)'
                    }}
                    onNotify={(n) => setNotification(n)}
                    formatCurrency={formatCurrency}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text-dark dark:text-text-light mb-2">
                  Notas Adicionales (opcional)
                </label>
                <textarea
                  className="w-full rounded-lg border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-text-dark dark:text-text-light focus:ring-primary focus:border-primary text-sm p-3 placeholder:font-light resize-none"
                  placeholder="Instrucciones especiales para la entrega, código de acceso, etc."
                  rows="3"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  disabled={submitting}
                />
              </div>
            </form>

            <div className="flex justify-start pt-4">
              <Link className="group text-sm font-medium flex items-center gap-2 text-gray-500 hover:text-primary transition-colors tracking-tight" to="/carrito">
                <span className="material-symbols-outlined text-[18px] transition-transform group-hover:-translate-x-1">arrow_back</span>
                Volver al Carrito
              </Link>
            </div>
          </div>

          <div className="w-full lg:w-[480px] flex flex-col gap-6">
            <div className="bg-accent-light dark:bg-gray-800/50 p-8 md:p-10 rounded-xl flex flex-col gap-6 sticky top-28 shadow-lg">
              <h2 className="text-3xl font-bold text-text-dark dark:text-text-light tracking-tight">Resumen del Pedido</h2>
              
              <div className="flex flex-col gap-6 border-b border-gray-200 dark:border-gray-700 pb-8 max-h-[300px] overflow-y-auto scrollbar-hide">
                {cartItems.map((item, index) => (
                  <div key={index} className="flex gap-6 items-center">
                    <div className="w-20 h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0 shadow-sm">
                      {item.image ? (
                        <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${getImageUrl(item.image)})` }}></div>
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="material-symbols-outlined text-gray-400 text-3xl">image</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 flex flex-col justify-center">
                      <h4 className="text-lg font-bold text-text-dark dark:text-text-light line-clamp-2">{item.productName}</h4>
                      <p className="text-sm text-gray-500 font-medium mt-1">Cant: {item.quantity}</p>
                      <p className="text-lg font-bold text-primary mt-1">{formatCurrency(item.price)}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-col gap-4 pb-8 border-b border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center text-base font-medium text-gray-600 dark:text-gray-300">
                  <span>Subtotal</span>
                  <span className="text-lg text-text-dark dark:text-text-light">{formatCurrency(displaySubtotal)}</span>
                </div>
                <div className="flex justify-between items-center text-base font-medium text-gray-600 dark:text-gray-300">
                  <span>Envío</span>
                  <span className="text-lg text-text-dark dark:text-text-light">
                    {isPickup || isNegotiation
                      ? (isNegotiation ? 'A acordar' : formatCurrency(0))
                      : shippingQuote.type === 'disabled'
                        ? 'No disponible'
                      : shippingQuote.type === 'pending'
                        ? (distanceStatus.message || 'Estamos calculando tu costo de envío…')
                        : (shippingQuote.type === 'free' && displayShipping === 0 ? 'Gratis' : formatCurrency(displayShipping))}
                  </span>
                </div>
                {selectedPaymentMethod?.label ? (
                  <div className="flex justify-between items-center text-base font-medium text-gray-600 dark:text-gray-300">
                    <span>Medio de pago</span>
                    <span className="text-lg text-text-dark dark:text-text-light">{selectedPaymentMethod.label}</span>
                  </div>
                ) : null}
                {displayAdjustment !== 0 ? (
                  <div className="flex justify-between items-center text-base font-medium text-gray-600 dark:text-gray-300">
                    <span>{displayAdjustment < 0 ? 'Descuento' : 'Recargo'}</span>
                    <span className={`text-lg ${displayAdjustment < 0 ? 'text-green-600' : 'text-orange-500'} font-semibold`}>
                      {displayAdjustment < 0 ? '-' : '+'}{formatCurrency(Math.abs(displayAdjustment))}
                    </span>
                  </div>
                ) : null}
                {/* Descuento - Se mostrará cuando el sistema de cupones esté implementado */}
                {/* 
                <div className="flex justify-between items-center text-sm font-light text-green-600">
                  <span>Descuento (NUEVOHOGAR)</span>
                  <span>-{formatCurrency(20)}</span>
                </div>
                */}
              </div>

              <div className="flex justify-between items-center pt-2">
                <span className="text-2xl font-bold text-text-dark dark:text-text-light">Total</span>
                <span className="text-4xl font-bold text-primary">{formatCurrency(displayTotal)}</span>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!canProceedToPay}
                className="w-full mt-4 h-14 bg-primary hover:bg-primary/90 text-white text-lg font-bold rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 flex items-center justify-center gap-3 tracking-wide disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? 'Procesando...' : paymentLocked ? 'Confirmá tu dirección' : 'Proceder al Pago'}
                {!submitting && !paymentLocked && <span className="material-symbols-outlined text-[24px] font-bold">arrow_forward</span>}
              </button>
              {selectedPaymentMethod?.type === 'gateway_mercadopago' && mercadoPagoRetryOrderId && (
                <button
                  type="button"
                  onClick={handleMercadoPagoRetry}
                  disabled={submitting}
                  className="w-full mt-2 h-12 bg-primary/80 hover:bg-primary/90 text-white text-base font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  Reintentar MercadoPago
                </button>
              )}

              <p className="text-xs text-center text-gray-400 font-light mt-2">
                Al continuar, aceptas nuestros términos y condiciones.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default Checkout
